import React, { useCallback, useEffect, useState } from "react";
import { Search, Loader2, LayoutGrid, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { SkeletonRows } from "@/components/common/Skeleton";
import { TID } from "@/lib/testIds";

export function FioriLibraryPanel() {
    const [modules, setModules] = useState([]);
    const [releases, setReleases] = useState([]);
    const [q, setQ] = useState("");
    const [module, setModule] = useState("");
    const [release, setRelease] = useState("");
    const [apps, setApps] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/fiori/modules"), api.get("/fiori/releases")]).then(([m, r]) => {
            setModules(m.data.modules);
            setReleases(r.data.releases);
        }).catch(() => {});
    }, []);

    const search = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 30 };
            if (q.trim()) params.q = q.trim();
            if (module) params.module = module;
            if (release) params.release = release;
            const { data } = await api.get("/fiori/search", { params });
            setApps(data.apps);
            setTotal(data.total);
        } catch {
            setApps([]);
        } finally {
            setLoading(false);
        }
    }, [q, module, release]);

    useEffect(() => { search(); }, [search]);

    return (
        <Panel
            eyebrow="Offline Reference Dataset · 17,226 real apps"
            title="SAP Fiori Apps Reference Library"
        >
            <p className="text-xs text-slate-500 font-mono mb-3">
                Bundled offline export from SAP&rsquo;s official Fiori Apps Reference Library — no live SAP connection.
                Search by App ID, App Name, or T-code; filter by module or S/4HANA release version.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        data-testid={TID.fioriSearchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && search()}
                        placeholder="App ID, App Name, or T-code…"
                        className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm pl-8 pr-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                    />
                </div>
                <select
                    data-testid={TID.fioriModuleFilter}
                    value={module}
                    onChange={(e) => setModule(e.target.value)}
                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                >
                    <option value="">All modules</option>
                    {modules.map((m) => (
                        <option key={m.code} value={m.code}>{`${m.code} · ${m.name} (${m.app_count})`}</option>
                    ))}
                </select>
                <select
                    data-testid={TID.fioriReleaseFilter}
                    value={release}
                    onChange={(e) => setRelease(e.target.value)}
                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none max-w-[220px]"
                >
                    <option value="">All S/4HANA releases</option>
                    {releases.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button
                    data-testid={TID.fioriSearchBtn}
                    onClick={search}
                    disabled={loading}
                    className="btn-primary sheen inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-60"
                >
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <LayoutGrid size={13} />}
                    Search
                </button>
            </div>
            <div className="text-[11px] font-mono text-slate-500 mb-2">{total.toLocaleString()} apps match</div>
            {loading ? (
                <SkeletonRows rows={6} cols={4} />
            ) : apps.length === 0 ? (
                <p className="text-sm text-slate-500 font-mono py-6 text-center">No Fiori apps match this query.</p>
            ) : (
                <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
                    {apps.map((a) => {
                        const isOpen = expanded === a.app_id;
                        return (
                            <div key={a.app_id} data-testid={TID.fioriAppRow(a.app_id)} className="panel p-3 rounded-[4px]">
                                <button onClick={() => setExpanded(isOpen ? null : a.app_id)} className="w-full flex items-center gap-2 text-left">
                                    {isOpen ? <ChevronDown size={14} className="text-cyan-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                                    <span className="font-mono text-xs text-cyan-300 shrink-0">{a.app_id}</span>
                                    <span className="text-sm text-slate-200 truncate flex-1">{a.app_name}</span>
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10 shrink-0">{a.module_name}</span>
                                    {a.status !== "Available" && (
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">{a.status}</span>
                                    )}
                                </button>
                                {isOpen && (
                                    <div className="mt-2 pt-2 border-t border-white/5 grid md:grid-cols-2 gap-2 text-[11px] font-mono text-slate-400">
                                        <div><span className="text-slate-500">T-codes:</span> {a.tcodes?.join(", ") || "—"}</div>
                                        <div><span className="text-slate-500">Business Role:</span> {a.business_role_desc || a.business_role || "—"}</div>
                                        <div><span className="text-slate-500">Catalog:</span> {a.business_catalog_title || "—"}</div>
                                        <div><span className="text-slate-500">Release:</span> {a.release_version || "—"}</div>
                                        <div><span className="text-slate-500">Backend Version:</span> {a.product_version_backend || "—"}</div>
                                        <div><span className="text-slate-500">UI Technology:</span> {a.ui_technology || "—"}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Panel>
    );
}
